import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useChangePasswordMutation } from "@/store/api/authApi";

const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Za-z]/, "Password must contain at least one letter")
    .matches(/\d/, "Password must contain at least one number")
    .notOneOf(
      [Yup.ref("currentPassword")],
      "New password must be different from the current password"
    )
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Please confirm your new password"),
});

const ChangePasswordCard = () => {
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [show, setShow] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const toggle = (key) =>
    setShow((prev) => ({ ...prev, [key]: !prev[key] }));

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        }).unwrap();
        toast.success("Password updated successfully");
        resetForm();
      } catch (err) {
        toast.error(
          err?.data?.message || "Failed to update password. Please try again."
        );
      }
    },
  });

  return (
    <Card
      sx={{
        borderRadius: 4,
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(20px)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        mt: 3,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <LockIcon sx={{ mr: 1, color: "#ff5722" }} />
          <Typography variant="h5" fontWeight={600}>
            Change Password
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          For your security, choose a strong password you don't use on any
          other site. You'll keep your current session.
        </Alert>

        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          autoComplete="off"
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Current password"
            name="currentPassword"
            type={show.current ? "text" : "password"}
            value={formik.values.currentPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.currentPassword &&
              Boolean(formik.errors.currentPassword)
            }
            helperText={
              formik.touched.currentPassword && formik.errors.currentPassword
            }
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => toggle("current")}
                    edge="end"
                    aria-label="toggle current password visibility"
                  >
                    {show.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="New password"
            name="newPassword"
            type={show.next ? "text" : "password"}
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.newPassword && Boolean(formik.errors.newPassword)
            }
            helperText={
              (formik.touched.newPassword && formik.errors.newPassword) ||
              "At least 8 characters, including a letter and a number."
            }
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => toggle("next")}
                    edge="end"
                    aria-label="toggle new password visibility"
                  >
                    {show.next ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Confirm new password"
            name="confirmPassword"
            type={show.confirm ? "text" : "password"}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.confirmPassword &&
              Boolean(formik.errors.confirmPassword)
            }
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => toggle("confirm")}
                    edge="end"
                    aria-label="toggle confirm password visibility"
                  >
                    {show.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={() => formik.resetForm()}
              disabled={isLoading}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading || !formik.isValid || !formik.dirty}
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              {isLoading ? "Updating..." : "Update password"}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordCard;
